/**
 * 导入预览里的**重复明细**（"哪几行、哪个字/词、会怎么处理"）—— 唯一口径的纯函数。
 *
 * ## 为什么需要它（用户原话）
 *
 * > "对于录表时的文本框中，如果有重复的字或词，但有提示，**几行，什么字或词**。
 * >  现在只提示重复，没有提示哪行，哪个字和词。"
 *
 * 用户真实的经历：他粘了 11 行，里面「上」出现了两次，预览只说一句
 * 「同一份里重复 1 条（已合并）」—— 他**不知道为什么**少了 1 条，也不知道
 * 是哪个字、在哪两行。于是"数字对不上"就变成了"这个功能不可信"。
 *
 * 所以这里必须回答三件事，缺一不可：
 *
 *   1. **是哪个字 / 哪个词**（`text`，词元原文，不改写、不归一）；
 *   2. **出现在哪几行**（`lines`：行号 + 该行出现次数 —— 同一行里写两遍也要说清）；
 *   3. **会怎么处理**（`action`：`merge` 合并成 1 条 / `skip` 已存在会跳过 /
 *      `update` 已存在会更新）。
 *
 * ## 三类重复（用户明确要求都要说清）
 *
 * | 类型 | 数据从哪来 | 函数 |
 * | --- | --- | --- |
 * | **同一份里重复·打错了**（份内合并） | 这一批解析出来的逐条字/词（**含**被合并掉的那些） | `groupImportDuplicates`（`action: 'merge'`） |
 * | **多音字重复**（有 2+ 个读音，只存 1 条） | 同上，但那个字是多音字 | `groupImportDuplicates`（`action: 'polyphonic'`） |
 * | **之前已经录过**（与库里已有重复） | 服务层逐条判定的结果行（`skipped` / `updated`） | `groupExistingDuplicates` |
 *
 * ⚠️ 后两类的前者与"打错"分开，是 2026-10-17 用户的原话：**多音字不是重复**。
 *    "打错的"与"多音字"都从**同一份数据**推导（服务层去重时登记的每一次出现），
 *    区别只在 `classifyImportDuplicate()` 那一句判定（内置拼音表）。
 * ⚠️ 三类都**只从"同一份数据"推导**：前两者的输入是服务层去重时用的那同一个键、
 *    后者的输入是服务层已经算好的 `skipped` / `updated` 行。界面**只负责渲染**，
 *    不许自己再解析一遍文本、也不许自己数一遍 —— 两处各算一遍迟早漂移，
 *    而这块信息的意义正是"说得清、对得上"。
 *
 * ## 这不是错误（⛔ 别把它做成阻断）
 *
 * 重复只会**合并 / 跳过**，行照常导入。所以：
 *   · 它**不进** `errors`（不进那张账的 `problem`）；
 *   · 界面用"提示"的色调渲染，**不要**用错误色，也不要因为它禁用「确认导入」。
 *
 * ## 为什么放在 shared
 *
 * 与 `import-counts.ts` 同一个理由：服务层算一次（预览与提交同一条代码路径），
 * 界面只渲染；而且 `apps/api` 与 `apps/web` 各自跑单测时能用同一份断言。
 */

import { availableReadings, isPolyphonic } from './listing.js';

// ---------------------------------------------------------------- 类型

/** 重复项是字还是词（与 `ImportRowReport.kind` 同口径）。 */
export type ImportDuplicateKind = 'char' | 'word';

/** 字行落在哪张表上（词行永远是 `null`：词不写收录）。 */
export type ImportDuplicateListType = 'shizi' | 'xiezi' | 'cibiao';

/**
 * **会怎么处理**：
 *   · `merge`      —— 同一份里重复、而且**这个字只有一个读音**（= 打错了），合并成 1 条；
 *   · `polyphonic` —— 同一份里重复、但这个字是**多音字**（有 2 个及以上读音）：
 *                     **不算重复**，单列一类；字表里仍然只存 1 条，因为读音是字的属性；
 *   · `skip`       —— 之前已经录过、内容一致，跳过（不会重复写入）；
 *   · `update`     —— 之前已经录过，但这次会更新它（拼音 / 收录 / 年级……）。
 */
export type ImportDuplicateAction = 'merge' | 'skip' | 'update' | 'polyphonic';

/**
 * 一次"出现"（服务层逐条处理时登记的一条）。
 *
 * ⚠️ 同一份里重复的字/词**每一次出现都要登记**（包括被合并掉的那几条）——
 *    少了它们就数不出"哪几行"，而那正是用户要的那句话。
 */
export interface ImportDuplicateOccurrence {
  kind: ImportDuplicateKind;
  /** 词元原文（原样，不做归一 —— 用户要照着它去原文里找）。 */
  text: string;
  /** 原文行号（1 开始）。 */
  line: number;
  /** 字行所在的表（`null` = 这一行没有指明表，或这是词行）。 */
  listType?: ImportDuplicateListType | null;
  /**
   * 分组作用域（服务层去重键里"哪本书"那一段）。
   *
   * ⚠️ 必须参与分组：同一个字在**两本书**上各出现一次**不是**重复
   *    （通用整块导入一次可以涉及多本书）。
   */
  scope?: string | null;
  /**
   * 字行**这个位置标定的读音**（迁移 0018；空 = 没标）。
   *
   * ⚠️ 它**参与分组**：同一个字出现两次、标了两个不同的音，是**两个位置**
   *    （不是重复录入）；只有"同字同音"才会被归成一条。
   */
  reading?: string | null;
}

/** 某个行号上出现了几次（同一行写两遍 → `times: 2`）。 */
export interface ImportDuplicateLine {
  line: number;
  times: number;
}

/** 预览里一条**重复明细**。 */
export interface ImportDuplicateItem {
  kind: ImportDuplicateKind;
  /** 哪个字 / 哪个词。 */
  text: string;
  /** 字行在哪张表上（`null` = 没指明 / 词行）。 */
  listType: ImportDuplicateListType | null;
  /** 分组作用域（多本书时用来区分；单本时界面可以不显示）。 */
  scope: string | null;
  action: ImportDuplicateAction;
  /**
   * 出现在哪几行（**按行号从小到大**，同一行只占一条 + `times`）。
   *
   * ⚠️ 列表**非空**：重复至少出现在 2 处，已存在的至少出现在 1 处。
   */
  lines: ImportDuplicateLine[];
  /**
   * 保留的是第几行那条。
   *
   * `merge` / `polyphonic` 时 = **第一次出现**的那一行（字表里留下的就是它）；
   * `skip` / `update` 时 = 它自己那一行（库里已经有了）。
   */
  keptLine: number;
  /**
   * 会被合并掉几条。
   *
   * `merge` / `polyphonic` 时 = 总出现数 − 1（多音字也是同一个字只存 1 条）；
   * `skip` / `update` 时 = 0（它们不是被合并掉的，是库里已经有了）。
   */
  mergedCount: number;
  /** 已存在但会更新时，服务层给的原因（如「已存在，更新 pinyin/收录（识字表）」）。 */
  reason: string | null;
  /**
   * 这个字**全部读音**（主读音在前）。只有 `action === 'polyphonic'` 时非空。
   *
   * ⚠️ 数据来自**内置拼音表**（`packages/shared/src/dictation/pinyin-data.ts`
   *    + `listing.ts` 的 `availableReadings()`），不查库、不需要迁移：
   *    读音是"字"的属性，字表里每个字仍然只有一行（`hanzi.pinyin` 存主读音，
   *    人工覆盖存在 `hanzi.pinyin_overridden`；按词定音存在 `word_hanzi.reading`）。
   */
  readings: string[];
}

/** 一条已存在行的最小形状（`ImportRowReport` 的前几个字段就够）。 */
export interface ImportDuplicateExistingRow {
  /** `char` / `word`（其它值按字处理，不会出现在这里）。 */
  kind: string;
  text: string;
  line: number;
  /** `create` / `update` / `skip` —— 只有 `update` / `skip` 算"之前已经录过"。 */
  action: string;
  reason?: string | null;
}

// ---------------------------------------------------------------- 分类：多音字 ≠ 打错

/**
 * 一次"重复"到底是**打错了**还是**多音字**。
 *
 * 用户原话（2026-10-17）：
 *
 * > "录入的字中有些确实是重复的字，因为我输错了，导致重复了。而有些是因为那个字是
 * >  **多音字，所以重复出现了**，这种情况，你就要**建立多音字**了，**不能把它简单
 * >  当作重复的字处理**。"
 *
 * 判据**只用内置拼音表**（`availableReadings` / `isPolyphonic`，见 `listing.ts`）：
 *
 *   · 字 + 有 2 个及以上读音 → `polyphonic`（读完 `readings` 交给界面列出来）；
 *   · 其余（只有一个读音 / 不是字 / 内置表没收录）→ `merge`（打错，合并 1 条）。
 *
 * ⚠️ **落库结果不变**：多音字在字表里仍然只存 1 条 —— 变的只是**分类与文案**。
 *    读音不是"第二行数据"，它是 `hanzi.pinyin`（主读音）那一行的属性；按词定音
 *    另存在 `word_hanzi.reading`。所以这里**不需要**、也**不应该**为它加迁移。
 *
 * ⚠️ 词（`kind === 'word'`）永远是 `merge`：同一个词写两遍就是重复，
 *    哪怕它包含多音字（`长大` 与 `成长` 是**两个不同的词**，本来就是两行，
 *    不会进到"重复"这一层 —— 去重键里带词元原文）。
 */
export function classifyImportDuplicate(
  kind: ImportDuplicateKind,
  text: string,
): { action: 'merge' | 'polyphonic'; readings: string[] } {
  if (kind !== 'char') return { action: 'merge', readings: [] };
  if (!isPolyphonic(text)) return { action: 'merge', readings: [] };
  return { action: 'polyphonic', readings: availableReadings(text) };
}

// ---------------------------------------------------------------- 同一份里重复

/** 分组键（与服务层批内去重的键同口径：作用域 + 字/词 + 表 + 词元 + 读音）。 */
function occurrenceKey(occurrence: ImportDuplicateOccurrence): string {
  const scope = occurrence.scope ?? '';
  const listType = occurrence.listType ?? '-';
  const reading = (occurrence.reading ?? '').trim();
  return `${scope}\u0000${occurrence.kind}\u0000${listType}\u0000${occurrence.text}\u0000${reading}`;
}

/**
 * **同一份里重复**的明细：把"出现过不止一次"的字/词挑出来，带上行号与处理方式。
 *
 * 返回的条目里 `action` 只有两种：
 *   · `merge`      —— 打错的重复（这个字只有一个读音）；
 *   · `polyphonic` —— 多音字重复（有 2+ 个读音，`readings` 非空）。
 * 服务层把这一个数组按 `action` 拆成 `duplicates.merged` 与
 * `duplicates.polyphonic` 两份（界面分别渲染、分别计数）。
 *
 * 输入是**这一批里每个字/词每一次出现**（服务层在去重那个循环里顺路登记）。
 * 输出保持"第一次出现的先后顺序"，界面按这个顺序列（与用户读原文的顺序一致）。
 *
 * 只出现一次的字/词**不出现在结果里**（它们不是重复）。
 */
export function groupImportDuplicates(
  occurrences: readonly ImportDuplicateOccurrence[],
): ImportDuplicateItem[] {
  const buckets = new Map<
    string,
    {
      item: Omit<ImportDuplicateItem, 'lines' | 'mergedCount'>;
      byLine: Map<number, number>;
      total: number;
    }
  >();

  for (const occurrence of occurrences) {
    if (occurrence.text === '') continue;
    const key = occurrenceKey(occurrence);
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      /**
       * ⚠️ 分类在**建桶时算一次**（`classifyImportDuplicate`），不要在这里猜：
       *    多音字的判据只有一份（内置拼音表），服务层用的也是这同一个函数。
       */
      const classification = classifyImportDuplicate(occurrence.kind, occurrence.text);
      bucket = {
        item: {
          kind: occurrence.kind,
          text: occurrence.text,
          listType: occurrence.listType ?? null,
          scope: occurrence.scope ?? null,
          action: classification.action,
          readings: classification.readings,
          /** 第一次出现的那一行 = **保留**的那一条（服务层就是这么判的）。 */
          keptLine: occurrence.line,
          reason: null,
        },
        byLine: new Map<number, number>(),
        total: 0,
      };
      buckets.set(key, bucket);
    }
    bucket.total += 1;
    bucket.byLine.set(occurrence.line, (bucket.byLine.get(occurrence.line) ?? 0) + 1);
  }

  const out: ImportDuplicateItem[] = [];
  for (const bucket of buckets.values()) {
    /** 只出现一次 = 不是重复（不列，免得"明细"变成整份清单）。 */
    if (bucket.total < 2) continue;
    out.push({
      ...bucket.item,
      lines: linesOf(bucket.byLine),
      mergedCount: bucket.total - 1,
    });
  }
  return out;
}

// ---------------------------------------------------------------- 之前已经录过

/**
 * **之前已经录过**的明细：把服务层判定成 `skip` / `update` 的行按字/词归并。
 *
 * ⚠️ 与上一类的区别：这里的行**本来就在库里**（不是这一批里的重复），所以
 *    每一条都会列出来（也允许只出现一次），并说清"跳过"还是"更新"。
 *    条数可能很多（重复导入同一份表时会有几百条），界面按
 *    `foldDuplicateItems()` 折叠，别把手机页面撑爆。
 */
export function groupExistingDuplicates(
  rows: readonly ImportDuplicateExistingRow[],
): ImportDuplicateItem[] {
  const buckets = new Map<
    string,
    {
      item: ImportDuplicateItem;
      byLine: Map<number, number>;
    }
  >();

  for (const row of rows) {
    if (row.action !== 'skip' && row.action !== 'update') continue;
    if (row.text === '') continue;
    const kind: ImportDuplicateKind = row.kind === 'word' ? 'word' : 'char';
    const key = `${kind}\u0000${row.text}`;
    const reason = row.reason ?? null;
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = {
        item: {
          kind,
          text: row.text,
          listType: null,
          scope: null,
          action: row.action === 'update' ? 'update' : 'skip',
          lines: [],
          keptLine: row.line,
          mergedCount: 0,
          reason,
          /** 这一类的处理方式是"跳过 / 更新"，与多音字无关 —— 不带读音。 */
          readings: [],
        },
        byLine: new Map<number, number>(),
      };
      buckets.set(key, bucket);
    } else if (row.action === 'update') {
      /**
       * 同一个字/词有多行时，"会更新"是**更强**的那条信息（它比"跳过"多做了事）——
       * 按它会做的处理说，别让用户以为"反正都跳过"。
       */
      bucket.item.action = 'update';
      if (reason !== null && reason !== '') bucket.item.reason = reason;
    }
    bucket.byLine.set(row.line, (bucket.byLine.get(row.line) ?? 0) + 1);
  }

  const out = [...buckets.values()].map((bucket) => ({
    ...bucket.item,
    lines: linesOf(bucket.byLine),
  }));
  /** 按第一次出现的行号排（与原文顺序一致，用户能顺着核对）。 */
  out.sort((a, b) => a.keptLine - b.keptLine);
  return out;
}

// ---------------------------------------------------------------- 渲染成人话

/** 行号 → 按行号排序的数组（同一行只占一条，带次数）。 */
function linesOf(byLine: ReadonlyMap<number, number>): ImportDuplicateLine[] {
  return [...byLine.entries()]
    .map(([line, times]) => ({ line, times }))
    .sort((a, b) => a.line - b.line);
}

/**
 * **出现在哪几行** —— 一句话（`第 3 行、第 7 行`；同一行多写一遍是
 * `第 4 行（2 次）`）。
 *
 * ⚠️ 行号是这块信息里最要紧的部分（用户原话就是"没有提示哪行"），
 *    所以界面上要给行号**加粗/高亮**，而不是混在一段灰字里。
 */
export function describeDuplicateLines(lines: readonly ImportDuplicateLine[]): string {
  return lines
    .map((entry) => (entry.times > 1 ? `第 ${entry.line} 行（${entry.times} 次）` : `第 ${entry.line} 行`))
    .join('、');
}

/**
 * **会怎么处理** —— 一句话。
 *
 * 三种说法与 `ImportDuplicateAction` 一一对应（⛔ 不要出现"错误 / 失败"字样：
 * 重复不会拦下任何一行）。
 */
export function describeDuplicateAction(item: ImportDuplicateItem): string {
  if (item.action === 'polyphonic') {
    /**
     * ⚠️ 文案要同时回答用户的两件事：**它有几个读音、是哪几个**，以及
     *    **为什么它还是一条**（读音是属性，不是重复行）。所以不说"合并"。
     */
    const readings = item.readings.length > 0 ? item.readings.join(' / ') : '（读音没拿到）';
    return `多音字（${item.readings.length} 个读音：${readings}）—— 字表里只存 1 条，保留第 ${item.keptLine} 行那一条（读音是字的属性，不算重复）`;
  }
  if (item.action === 'merge') {
    return `合并成 1 条，保留第 ${item.keptLine} 行那一条（另外 ${item.mergedCount} 条不再单独入库）`;
  }
  if (item.action === 'update') {
    return item.reason === null || item.reason === ''
      ? '之前已经录过 —— 会更新这一条'
      : `之前已经录过 —— 会更新（${item.reason}）`;
  }
  return '之前已经录过 —— 跳过，不会重复写入';
}

/**
 * 多音字明细的**一行清单**（`地（dì / de）、数（shù / shǔ / shuò）`）。
 *
 * 用户原话："**多音字有几个（还要列出来）**" —— 所以界面上既有条数，也有这一句。
 * 传进来的应当只有 `action === 'polyphonic'` 的条目（后端已经分好类，
 * 见 `DictationImportReport.duplicates.polyphonic`）。
 *
 * ⚠️ 2026-10-20：用户对「按书录入」预览里那个**蓝底汇总块**改了口径 ——
 *    "那节多音字后面，**你那节拼音就不要列出来了，只需要列出字就可以了**"。
 *    那一块改用下面的 `describePolyphonicChars()`；本函数**保留**给
 *    「整块导入（高级）」面板与重复明细用（别处没让改，⛔ 不要一起改掉）。
 */
export function describePolyphonicDuplicates(items: readonly ImportDuplicateItem[]): string {
  return items
    .filter((item) => item.action === 'polyphonic')
    .map((item) => `${item.text}（${item.readings.join(' / ')}）`)
    .join('、');
}

/**
 * 多音字明细的**只列字**版本（`地、数`）—— **不带读音**。
 *
 * 用户原话（2026-10-20，指「按书录入」预览里那个蓝底汇总块）：
 *
 * > "目前显示「一共有几个字（多音字只算一次）」，下面会列出多音字重复出现的几个，
 * >  后面具体列出了那几个多音字 —— 这没问题。但那节多音字后面，**你那节拼音就不要
 * >  列出来了，只需要列出字就可以了**。"
 *
 * 所以这一句只有字：`多音字（重复出现的）：2 个 —— 地、数`。
 *
 * ⚠️ 与 `describePolyphonicDuplicates()` 的分工：**只有这个蓝底块**用本函数；
 *    重复明细 / 高级导入面板仍然要带读音（用户没说改那里）。
 */
export function describePolyphonicChars(items: readonly ImportDuplicateItem[]): string {
  return items
    .filter((item) => item.action === 'polyphonic')
    .map((item) => item.text)
    .join('、');
}

// ---------------------------------------------------------------- 拆分与计数（服务层用）

/** 份内重复按**两类**拆开之后的样子（服务层直接写进报告/账里）。 */
export interface ImportDuplicateGroups {
  /** **打错了**的重复（这个字/词只有一个读音），会合并成 1 条。 */
  merged: ImportDuplicateItem[];
  /** **多音字**的重复（这个字有 2+ 个读音）：不算重复，字表里仍只存 1 条。 */
  polyphonic: ImportDuplicateItem[];
}

/**
 * 把 `groupImportDuplicates()` 的结果按 `action` 拆成"打错"与"多音字"两组。
 *
 * ⚠️ 服务层的两个计数（`merged` / `mergedPolyphonic`）与两块明细都从这**一个**
 *    数组派生 —— 于是"账上的条数"与"下面列出来的条数"结构上不可能对不上。
 */
export function splitImportDuplicates(
  items: readonly ImportDuplicateItem[],
): ImportDuplicateGroups {
  return {
    merged: items.filter((item) => item.action !== 'polyphonic'),
    polyphonic: items.filter((item) => item.action === 'polyphonic'),
  };
}

/**
 * 一组重复明细**一共合并掉多少条**（= 各条 `mergedCount` 之和）。
 *
 * `mergedCount = 这个条目的出现次数 − 1`，所以把一组加起来就是"这一组实际少写了几行"。
 * 服务层用它填 `ImportCountInput.merged` / `mergedPolyphonic`（恒等式左边那两项）。
 */
export function sumDuplicateMerges(items: readonly ImportDuplicateItem[]): number {
  return items.reduce((total, item) => total + item.mergedCount, 0);
}

/**
 * 一条重复明细的**整句话**（"字「上」出现在第 3 行、第 7 行 —— 合并成 1 条……"）。
 *
 * 界面按字段分开排版（行号单独高亮），这句话留给日志 / 测试 / 折叠视图的
 * `title` 属性 —— 两处用**同一句**文案，免得"看得到的"和"念出来的"不一样。
 */
export function describeDuplicateItem(item: ImportDuplicateItem): string {
  const unit = item.kind === 'char' ? '字' : '词';
  return `${unit}「${item.text}」出现在${describeDuplicateLines(item.lines)} —— ${describeDuplicateAction(item)}`;
}

// ---------------------------------------------------------------- 折叠

/**
 * 超过几条**先折起来**（重复导入整本书时"已录过"会有几百条，全铺开会把手机页面撑爆）。
 *
 * `6` 的依据：一屏手机上，一条明细占两行，6 条 ≈ 12 行 ≈ 半屏 ——
 * 再多就要先看到"共多少条"再决定展不展开。
 */
export const IMPORT_DUPLICATE_FOLD_THRESHOLD = 6;

/** 折叠结果（`hidden` = 还有多少条没渲染，`total` = 一共多少条）。 */
export interface FoldedImportDuplicates {
  shown: ImportDuplicateItem[];
  hidden: number;
  total: number;
}

/**
 * 折叠：默认只给前 `limit` 条 + "还有多少条"。
 *
 * ⚠️ 折叠**不能**丢掉数量（`total` 必须原样带出来）：用户核对的是"共几条"，
 *    只显示前 6 条而不说总数，等于又一次"只提示重复、不说清"。
 */
export function foldDuplicateItems(
  items: readonly ImportDuplicateItem[],
  limit: number = IMPORT_DUPLICATE_FOLD_THRESHOLD,
): FoldedImportDuplicates {
  const total = items.length;
  const cut = Number.isFinite(limit) ? Math.max(0, Math.trunc(limit)) : 0;
  return {
    shown: items.slice(0, cut),
    hidden: Math.max(0, total - cut),
    total,
  };
}
